import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useGetUserID } from '../hooks/useGetUserID';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import '../styles/ShoppingList.css';
export default function ShoppingList() {
    const [cookies] = useCookies(['access_token']);
    const userID = useGetUserID();
    const navigate = useNavigate();
    
    // Token validation state
    const [tokenValidated, setTokenValidated] = useState(false);
    const [tokenValidationAttempted, setTokenValidationAttempted] = useState(false);
    
    const [shoppingList, setShoppingList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groupedItems, setGroupedItems] = useState({});
    const [showExcessSummary, setShowExcessSummary] = useState(false);
    const [swipeStates, setSwipeStates] = useState({});

    // Helper to get auth token from cookies or localStorage
    const getAuthToken = () => cookies.access_token || window.localStorage.getItem('auth_token');

    // Helper to build Authorization header only when token exists
    const getAuthHeaders = () => {
        const token = getAuthToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // Token validation effect - similar to CreateRecipe.js
    useEffect(() => {
        const validateToken = () => {
            // Check both cookie and localStorage for a token
            const cookieToken = cookies.access_token;
            const localStorageToken = localStorage.getItem('auth_token');
            const hasToken = !!cookieToken || !!localStorageToken;

            console.log('ShoppingList token validation check:', {
                inCookie: !!cookieToken,
                inLocalStorage: !!localStorageToken,
                hasToken
            });

            if (!hasToken) {
                console.log('No auth token available for ShoppingList');
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                navigate('/auth');
                return;
            }

            // Get the token to validate (prefer cookie, fallback to localStorage)
            const token = cookieToken || localStorageToken;
            
            try {
                // Manually validate the token by decoding and checking expiration
                const parts = token.split('.');
                if (parts.length !== 3) {
                    throw new Error('Invalid token format');
                }
                
                // Decode the payload
                const payload = JSON.parse(atob(parts[1]));
                const now = Math.floor(Date.now() / 1000);
                
                // Check if token is expired
                if (payload.exp && payload.exp < now) {
                    console.log('Token has expired in ShoppingList');
                    setTokenValidated(false);
                    setTokenValidationAttempted(true);
                    setError('Your session has expired. Please log in again.');
                    navigate('/auth');
                    return;
                }
                
                console.log('ShoppingList token validated successfully');
                setTokenValidated(true);
                setTokenValidationAttempted(true);
                setError(null); // Clear any previous session errors
                
            } catch (tokenError) {
                console.error('Token validation failed in ShoppingList:', tokenError);
                setTokenValidated(false);
                setTokenValidationAttempted(true);
                setError('Authentication error. Please log in again.');
                navigate('/auth');
            }
        };

        // Run validation immediately
        validateToken();
        
    }, [cookies.access_token, navigate]);

    // Listen for auth token updates from login
    useEffect(() => {
        const handleAuthTokenUpdate = (event) => {
            console.log('ShoppingList: Auth token updated, refreshing auth state');
            // Force re-validation of the new token
            setTokenValidated(false);
            setTokenValidationAttempted(false);
        };

        window.addEventListener('auth-token-updated', handleAuthTokenUpdate);
        
        return () => {
            window.removeEventListener('auth-token-updated', handleAuthTokenUpdate);
        };
    }, []);

    useEffect(() => {
        // Only fetch shopping list if token validation is complete and token is valid
        if (!tokenValidationAttempted) {
            return; // Wait for token validation
        }

        if (!userID) {
            navigate('/auth');
            return;
        }

        if (tokenValidated) {
            fetchShoppingList();
        }
    }, [userID, tokenValidated, tokenValidationAttempted]);

    const fetchShoppingList = async () => {
        if (!userID) {
            setError('Please log in to view your shopping list');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/shopping-list`, {
                headers: getAuthHeaders()
            });
            
            setShoppingList(response.data);
            
            // Group items by name for easy management
            const grouped = response.data.reduce((acc, item) => {
                if (!acc[item.name]) {
                    acc[item.name] = [];
                }
                acc[item.name].push(item);
                return acc;
            }, {});
            
            setGroupedItems(grouped);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching shopping list:', error);
            setError('Failed to load shopping list');
            setLoading(false);
        }
    };

    const removeItem = async (itemId) => {
        try {
            await axios.delete(`${API_BASE_URL}/shopping-list/${itemId}`, {
                headers: getAuthHeaders()
            });
            
            // Update local state
            setShoppingList(prev => prev.filter(item => item._id !== itemId));
            
            // Update groupedItems
            const updatedGroups = {...groupedItems};
            Object.keys(updatedGroups).forEach(name => {
                updatedGroups[name] = updatedGroups[name].filter(item => item._id !== itemId);
                if (updatedGroups[name].length === 0) {
                    delete updatedGroups[name];
                }
            });
            
            setGroupedItems(updatedGroups);
        } catch (error) {
            console.error('Error removing item:', error);
        }
    };

    const clearShoppingList = async () => {
        try {
            await axios.delete(`${API_BASE_URL}/shopping-list/clear-all`, {
                headers: getAuthHeaders()
            });
            
            setShoppingList([]);
            setGroupedItems({});
        } catch (error) {
            console.error('Error clearing shopping list:', error);
        }
    };

    const updateIngredientDetails = async (itemId, field, value) => {
        try {
            const updateData = { [field]: value };
            
            await axios.put(
                `${API_BASE_URL}/shopping-list/${itemId}`, 
                updateData,
                {
                    headers: getAuthHeaders()
                }
            );
            
            // Update local state
            setShoppingList(prev => prev.map(item => 
                item._id === itemId 
                    ? {...item, [field]: value}
                    : item
            ));
            
            // Update grouped items
            const updatedGroups = {...groupedItems};
            Object.keys(updatedGroups).forEach(name => {
                updatedGroups[name] = updatedGroups[name].map(item => 
                    item._id === itemId 
                        ? {...item, [field]: value}
                        : item
                );
            });
            
            setGroupedItems(updatedGroups);
        } catch (error) {
            console.error(`Error updating item ${field}:`, error);
        }
    };

    const completeShoppingAndAddToCupboard = async () => {
        try {
            const itemIds = shoppingList.map(item => item._id);
            
            if (itemIds.length === 0) {
                return;
            }
            
            const response = await axios.post(
                `${API_BASE_URL}/shopping-list/complete-shopping`,
                { itemIds: itemIds },
                {
                    headers: getAuthHeaders()
                }
            );
            
            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to complete shopping');
            }
            
            setShoppingList([]);
            setGroupedItems({});
            
        } catch (error) {
            console.error('Error completing shopping:', error);
        }
    };

    const formatAmount = (amount, unit) => {
        if (unit?.toLowerCase() === 'kg') {
            return amount >= 1 ? `${amount.toFixed(2)}kg` : `${(amount * 1000).toFixed(0)}g`;
        } else if (unit?.toLowerCase() === 'g') {
            return amount >= 1000 ? `${(amount / 1000).toFixed(2)}kg` : `${amount.toFixed(0)}g`;
        } else if (unit?.toLowerCase() === 'l') {
            return amount >= 1 ? `${amount.toFixed(2)}l` : `${(amount * 1000).toFixed(0)}ml`;
        } else if (unit?.toLowerCase() === 'ml') {
            return amount >= 1000 ? `${(amount / 1000).toFixed(2)}l` : `${amount.toFixed(0)}ml`;
        } else {
            return `${amount.toFixed(2)}${unit}`;
        }
    };

    // Touch/Swipe handlers for mobile interactions
    const handleTouchStart = (itemId, e) => {
        const touch = e.touches[0];
        setSwipeStates(prev => ({
            ...prev,
            [itemId]: {
                startX: touch.clientX,
                startY: touch.clientY,
                startTime: Date.now()
            }
        }));
    };

    const handleTouchEnd = (itemId, e) => {
        const swipeState = swipeStates[itemId];
        if (!swipeState) return;

        const touch = e.changedTouches[0];
        const deltaX = touch.clientX - swipeState.startX;
        const deltaY = touch.clientY - swipeState.startY;
        const deltaTime = Date.now() - swipeState.startTime;

        // Check for swipe gesture (horizontal swipe, fast enough, long enough)
        if (Math.abs(deltaX) > Math.abs(deltaY) && 
            Math.abs(deltaX) > 50 && 
            deltaTime < 300) {
            
            if (deltaX > 0) {
                // Swipe right - increase quantity
                const item = shoppingList.find(item => item._id === itemId);
                if (item) {
                    const currentQuantity = parseInt(item.quantity) || 1;
                    updateIngredientDetails(itemId, 'quantity', currentQuantity + 1);
                }
            } else {
                // Swipe left - decrease quantity or remove if quantity is 1
                const item = shoppingList.find(item => item._id === itemId);
                if (item) {
                    const currentQuantity = parseInt(item.quantity) || 1;
                    if (currentQuantity > 1) {
                        updateIngredientDetails(itemId, 'quantity', currentQuantity - 1);
                    } else {
                        // Show confirmation or remove directly
                        removeItem(itemId);
                    }
                }
            }
        }

        // Clear swipe state
        setSwipeStates(prev => {
            const newState = { ...prev };
            delete newState[itemId];
            return newState;
        });
    };

    const handleLongPress = (itemId) => {
        // Long press to remove item with haptic feedback if available
        if (navigator.vibrate) {
            navigator.vibrate(50);
        }
        removeItem(itemId);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-recipe-light flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-recipe-primary mx-auto mb-4"></div>
                    <p className="text-recipe-dark">Loading shopping list...</p>
                </div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="min-h-screen bg-recipe-light flex items-center justify-center p-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
                    <div className="text-red-500 text-2xl mb-2">⚠️</div>
                    <p className="text-red-700">{error}</p>
                </div>
            </div>
        );
    }
    
    if (!userID) {
        return (
            <div className="min-h-screen bg-recipe-light flex items-center justify-center p-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center max-w-md">
                    <div className="text-yellow-500 text-2xl mb-2">🔒</div>
                    <p className="text-yellow-700">Please log in to view your shopping list</p>
                </div>
            </div>
        );
    }

    if (shoppingList.length === 0) {
        return (
            <div className="min-h-screen bg-recipe-light p-4">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-lg shadow-recipe p-6 text-center">
                        <div className="text-4xl mb-4">🛒</div>
                        <h1 className="text-2xl font-bold text-recipe-dark mb-2">Shopping List</h1>
                        <p className="text-gray-600 mb-4">Your shopping list is empty</p>
                        <p className="text-sm text-gray-500">Add ingredients from recipe pages by clicking "Order All Ingredients"</p>
                    </div>
                </div>
            </div>
        );
    }

    // Calculate totals
    const calculations = shoppingList.reduce((acc, item) => {
        const quantity = parseInt(item.quantity) || 1;
        const cost = parseFloat(item.cost) || 0;
        const originalCost = parseFloat(item.originalCost) || cost;
        const savings = parseFloat(item.savings) || 0;
        
        // For multi-buy deals, don't multiply by quantity since cost is already total
        const isMultiBuyDeal = (item.specialInfo && item.specialInfo.type === 'MULTI_BUY') || 
                              (item.userSelectedSpecial && item.userChoice === 'special');
        const itemCost = isMultiBuyDeal ? cost : cost * quantity;
        const itemOriginalCost = isMultiBuyDeal ? originalCost : originalCost * quantity;
        const itemSavings = isMultiBuyDeal ? savings : savings * quantity;
        
        return {
            totalCost: acc.totalCost + itemCost,
            totalOriginalCost: acc.totalOriginalCost + itemOriginalCost,
            totalSavings: acc.totalSavings + itemSavings
        };
    }, { totalCost: 0, totalOriginalCost: 0, totalSavings: 0 });

    const { totalCost, totalOriginalCost, totalSavings } = calculations;
    const excessItems = shoppingList.filter(item => item.excessAmount > 0);

    return (
        <div className="min-h-screen bg-recipe-light">
            {/* Print Header - Only visible when printing */}
            <div className="print-header hidden print:block" style={{margin: 0, padding: 0}}>
                <h1 style={{margin: 0, padding: 0}}>Shopping List</h1>
                <p className="text-sm text-gray-600 mt-1" style={{margin: '5px 0 0 0', padding: 0}}>
                    Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                </p>
            </div>

            {/* Header */}
            <div className="bg-white shadow-sm border-b sticky top-0 z-10 print:hidden">
                <div className="max-w-2xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl font-bold text-recipe-dark">Shopping List</h1>
                        <div className="flex gap-2">
                            <button 
                                onClick={() => window.print()}
                                className="px-3 py-1.5 text-sm bg-recipe-primary hover:bg-recipe-primary/90 text-white rounded-lg transition-colors print:hidden"
                            >
                                Print
                            </button>
                            <button 
                                onClick={clearShoppingList}
                                className="px-3 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors print:hidden"
                            >
                                Clear
                            </button>
                            <button 
                                onClick={completeShoppingAndAddToCupboard}
                                className="px-3 py-1.5 text-sm bg-recipe-success hover:bg-recipe-success/90 text-white rounded-lg transition-colors print:hidden"
                            >
                                Complete
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto p-4 space-y-4 print-content">
                {/* Mobile Instructions */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200 print:hidden md:hidden">
                    <div className="flex items-center gap-2 text-sm text-blue-700">
                        <span className="text-lg">👆</span>
                        <span className="font-medium">Swipe right to add more, swipe left to remove items</span>
                    </div>
                </div>

                {/* Summary Card */}
                <div className="bg-white rounded-lg shadow-recipe p-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-600">Total Items: {shoppingList.length}</span>
                        <div className="text-right">
                            <div className="text-lg font-bold text-recipe-success">
                                R{totalCost.toFixed(2)}
                            </div>
                            {totalSavings > 0 && (
                                <div className="text-xs text-gray-500">
                                    <span className="line-through">R{totalOriginalCost.toFixed(2)}</span>
                                    <span className="text-recipe-success ml-1">
                                        Save R{totalSavings.toFixed(2)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Excess Summary */}
                    {excessItems.length > 0 && (
                        <div className="border-t pt-3">
                            <button
                                onClick={() => setShowExcessSummary(!showExcessSummary)}
                                className="flex items-center justify-between w-full text-left"
                            >
                                <span className="text-sm font-medium text-blue-600">
                                     {excessItems.length} leftover ingredient{excessItems.length > 1 ? 's' : ''} for cupboard
                                </span>
                                <span className="text-blue-600">
                                    {showExcessSummary ? '▼' : '▶'}
                                </span>
                            </button>
                            
                            {showExcessSummary && (
                                <div className="mt-2 space-y-1">
                                    {excessItems.map(item => {
                                        const totalExcess = item.excessAmount * (parseInt(item.quantity) || 1);
                                        return (
                                            <div key={item._id} className="text-xs text-gray-600 bg-blue-50 rounded px-2 py-1">
                                                {item.name}: {formatAmount(totalExcess, item.excessUnit)}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Shopping Items */}
                <div className="space-y-2 shopping-list-container">
                    {shoppingList.map(item => {
                        let quantity = parseInt(item.quantity) || 1;
                        
                        // Force quantity to 1 for multi-buy deals if it's somehow different
                        const isMultiBuyDeal = (item.specialInfo && item.specialInfo.type === 'MULTI_BUY') || 
                                              (item.userSelectedSpecial && item.userChoice === 'special');
                        
                        if (isMultiBuyDeal && quantity !== 1) {
                            console.log(`🔧 Fixing quantity for multi-buy deal ${item.name}: ${quantity} → 1`);
                            quantity = 1;
                            // Update the database to fix the quantity
                            updateIngredientDetails(item._id, 'quantity', 1);
                        }
                        const handleQuantityChange = (newQuantity) => {
                            // Prevent quantity changes for multi-buy deals
                            const isMultiBuyDeal = (item.specialInfo && item.specialInfo.type === 'MULTI_BUY') || 
                                                  (item.userSelectedSpecial && item.userChoice === 'special');
                            
                            if (isMultiBuyDeal) {
                                console.log(`⚠️ Cannot change quantity for multi-buy deal: ${item.name}`);
                                return; // Don't allow quantity changes for multi-buy deals
                            }
                            
                            if (newQuantity >= 1) {
                                updateIngredientDetails(item._id, 'quantity', newQuantity);
                            }
                        };
                        
                        // Debug logging for multi-buy items
                        if (item.name && item.name.toLowerCase().includes('kidney')) {
                            console.log(`🔍 Shopping list item ${item.name}:`, {
                                quantity: item.quantity,
                                cost: item.cost,
                                specialInfo: item.specialInfo,
                                hasSpecialInfo: !!item.specialInfo,
                                specialType: item.specialInfo?.type
                            });
                        }

                        return (
                            <div 
                                key={item._id} 
                                className="shopping-item-card"
                                onTouchStart={(e) => handleTouchStart(item._id, e)}
                                onTouchEnd={(e) => handleTouchEnd(item._id, e)}
                            >
                                {/* Compact Item Layout */}
                                <div className="shopping-item-content">
                                    {/* Small Image */}
                                    <div className="item-image-mini">
                                        {item.imageUrl ? (
                                            <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                className="item-image-mini-img"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextElementSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div className="item-image-mini-placeholder" style={{ display: item.imageUrl ? 'none' : 'flex' }}>
                                            🛒
                                        </div>
                                    </div>

                                    {/* Item Info */}
                                    <div className="item-info-compact">
                                        <div className="item-name-compact">{item.brandName || item.name}</div>
                                        <div className="item-details-compact">
                                            <span className="item-brand-compact">{item.brand || 'Generic'}</span>
                                            {item.specialInfo && (
                                                <span className="special-indicator">🏷️</span>
                                            )}
                                            {item.excessAmount > 0 && (
                                                <span className="excess-indicator"></span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="item-actions-compact">
                                        {/* Quantity Controls */}
                                        <div className="quantity-controls-mini">
                                            {/* For multi-buy deals, show deal info instead of quantity controls */}
                                            {(item.specialInfo && item.specialInfo.type === 'MULTI_BUY') || 
                                             (item.userSelectedSpecial && item.userChoice === 'special') ? (
                                                <div className="deal-info-mini">
                                                    <span className="deal-text">
                                                        {item.specialInfo?.buyQuantity || item.specialInfo?.totalPackagesPurchased || item.packagesNeeded || 2}x Deal
                                                    </span>
                                                </div>
                                            ) : (
                                                <>
                                                    <button 
                                                        onClick={() => handleQuantityChange(quantity - 1)} 
                                                        className="qty-btn-mini qty-btn-minus"
                                                        disabled={quantity <= 1}
                                                    >
                                                        −
                                                    </button>
                                                    <span className="qty-display">{quantity}</span>
                                                    <button 
                                                        onClick={() => handleQuantityChange(quantity + 1)} 
                                                        className="qty-btn-mini qty-btn-plus"
                                                    >
                                                        +
                                                    </button>
                                                </>
                                            )}
                                        </div>

                                        {/* Price */}
                                        <div className="price-compact">
                                            {/* For multi-buy deals, don't multiply by quantity since cost is already total */}
                                            {(item.specialInfo && item.specialInfo.type === 'MULTI_BUY') || 
                                             (item.userSelectedSpecial && item.userChoice === 'special') ? (
                                                <>
                                                    <div className="current-price-compact">R{parseFloat(item.cost).toFixed(2)}</div>
                                                    {item.originalCost && item.originalCost > item.cost && (
                                                        <div className="original-price-compact">R{parseFloat(item.originalCost).toFixed(2)}</div>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <div className="current-price-compact">R{(parseFloat(item.cost) * quantity).toFixed(2)}</div>
                                                    {item.originalCost && item.originalCost > item.cost && (
                                                        <div className="original-price-compact">R{(parseFloat(item.originalCost) * quantity).toFixed(2)}</div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Remove Button */}
                                        <button 
                                            onClick={() => removeItem(item._id)} 
                                            className="remove-btn-mini"
                                            aria-label="Remove item"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>

                                {/* Expandable Details (for special offers, excess, etc.) */}
                                {(item.specialInfo || item.excessAmount > 0) && (
                                    <div className="item-details-expandable">
                                        {item.specialInfo && (
                                            <div className="special-offer-mini">
                                                {item.specialInfo.type === 'MULTI_BUY' && (
                                                    <span>🛍️ Buy {item.specialInfo.buyQuantity} for R{item.specialInfo.specialPrice}</span>
                                                )}
                                                {item.specialInfo.type === 'SPECIAL_PRICE' && (
                                                    <span>💰 Special: R{item.specialInfo.specialPrice}</span>
                                                )}
                                            </div>
                                        )}
                                        {item.excessAmount > 0 && (
                                            <div className="excess-info-mini">
                                                 Leftover: {formatAmount(item.excessAmount, item.excessUnit)}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Print Button - Removed, now in header */}
            </div>

            {/* Enhanced Print Styles */}
            <style jsx>{`
                @media print {
                    /* Complete reset */
                    * {
                        margin: 0 !important;
                        padding: 0 !important;
                        box-sizing: border-box !important;
                    }
                    
                    body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                        font-size: 12px !important;
                        line-height: 1.4 !important;
                        min-height: auto !important;
                    }
                    
                    /* Hide interactive elements */
                    button, input, select, .print\\:hidden {
                        display: none !important;
                    }
                    
                    /* Main container */
                    .min-h-screen {
                        min-height: auto !important;
                        height: auto !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                    }
                    
                    .bg-recipe-light {
                        background: white !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    
                    /* Print header - minimal */
                    .print-header {
                        display: block !important;
                        text-align: center !important;
                        margin: 0 0 10px 0 !important;
                        padding: 0 0 10px 0 !important;
                        border-bottom: 2px solid #333 !important;
                    }
                    
                    .print-header h1 {
                        font-size: 18px !important;
                        font-weight: bold !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    
                    .print-header p {
                        font-size: 11px !important;
                        margin: 5px 0 0 0 !important;
                        padding: 0 !important;
                    }
                    
                    /* Content container */
                    .print-content {
                        margin: 0 !important;
                        padding: 0 !important;
                        max-width: none !important;
                        width: 100% !important;
                    }
                    
                    /* Remove all spacing */
                    .space-y-4, .space-y-3 {
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    
                    .space-y-4 > * + *,
                    .space-y-3 > * + * {
                        margin-top: 0 !important;
                    }
                    
                    /* Summary card */
                    .space-y-4 > .bg-white:first-of-type {
                        background: white !important;
                        border: 2px solid #333 !important;
                        margin: 0 0 10px 0 !important;
                        padding: 10px !important;
                    }
                    
                    /* Shopping items */
                    .shopping-list-container {
                        margin: 0 !important;
                        padding: 0 !important;
                        page-break-inside: auto !important;
                    }
                    
                    .shopping-list-group {
                        background: white !important;
                        border: 1px solid #ddd !important;
                        margin: 0 0 8px 0 !important;
                        padding: 0 !important;
                        page-break-inside: avoid !important;
                        page-break-after: auto !important;
                        break-inside: avoid !important;
                    }
                    
                    .bg-gray-50 {
                        background: #f9f9f9 !important;
                        border-bottom: 1px solid #ddd !important;
                        padding: 8px 12px !important;
                        margin: 0 !important;
                    }
                    
                    .p-4 {
                        padding: 8px 12px !important;
                        margin: 0 !important;
                        page-break-inside: avoid !important;
                    }
                    
                    /* Ensure individual ingredient items stay together */
                    .border-b {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    
                    /* Text sizes */
                    .text-sm { font-size: 11px !important; }
                    .text-xs { font-size: 10px !important; }
                    .text-lg { font-size: 14px !important; }
                    
                    /* Layout */
                    .flex { display: block !important; }
                    .flex-1 { width: 100% !important; }
                    .rounded-lg { border-radius: 0 !important; }
                    .overflow-hidden { overflow: visible !important; }
                    
                    /* Ensure all content is visible */
                    * {
                        overflow: visible !important;
                    }
                    
                    /* Remove any height restrictions */
                    .min-h-screen, .h-screen {
                        min-height: auto !important;
                        height: auto !important;
                    }
                    
                    /* Page settings */
                    @page {
                        margin: 0.5in;
                        size: A4;
                    }
                    
                    /* Ensure no blank pages */
                    .print-header + div {
                        margin-top: 0 !important;
                        padding-top: 0 !important;
                    }
                    
                    /* Force page breaks only when necessary */
                    .shopping-list-group:nth-child(8n) {
                        page-break-after: always !important;
                    }
                    
                    /* Ensure minimum content on each page */
                    .shopping-list-container {
                        orphans: 2 !important;
                        widows: 2 !important;
                    }
                }
                
                /* Mobile optimizations */
                @media (max-width: 768px) {
                    .max-w-2xl {
                        max-width: 100% !important;
                        padding: 0 8px !important;
                    }
                    
                    .p-4 {
                        padding: 12px !important;
                    }
                    
                    .flex.gap-2 {
                        gap: 4px !important;
                    }
                    
                    .px-3.py-1\\.5 {
                        padding: 6px 8px !important;
                        font-size: 12px !important;
                    }
                }
            `}</style>
        </div>
    );
} 